import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Spin, Typography } from 'antd';
import { ReactComponent as EmptyChartIllustration } from '../../assets/empty-chart.svg';
import { useSelected } from '../../providers/selectedContext';
import {
  getGheAnalogueTables
} from '../../api/data';
import { GheAnalogueTablesPayload } from '../../@types/gheAnalogueTables/types';
import { GheTargetAnalogueTable } from './GheTargetAnalogueTable';
import { RockId } from '../../@types/gheInputs/types';
import { Button, Tab, TabList, Tabs } from '../../design-system';
import styles from '../gheShared/gheStickyHeader.module.css';
import navStyles from './GheAnalogueTablesTab.module.css';

const ROCK_IDS: RockId[] = [
  'GHE1',
  'GHE2',
  'GHE3',
  'GHE4',
  'GHE5',
  'GHE6',
  'GHE7',
  'GHE8',
  'GHE9',
  'GHE10'
];

export const GheAnalogueTablesTab = ({ actions }: { actions?: import('react').ReactNode }) => {
  const { selectedInfo } = useSelected();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<GheAnalogueTablesPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const projectUid = selectedInfo.project?.uid;
    if (!projectUid) {
      setPayload(null);
      return;
    }

    let isMounted = true;
    const loadTables = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getGheAnalogueTables(projectUid);
        if (!isMounted) {
          return;
        }
        setPayload(response);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError('Unable to load analogue tables. Please verify previous workflow steps.');
        setPayload(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTables();
    return () => {
      isMounted = false;
    };
  }, [selectedInfo.project?.uid]);

  const [activeRockId, setActiveRockId] = useState<RockId>('GHE1');
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Partial<Record<RockId, HTMLDivElement | null>>>({});

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [payload]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || headerHeight === 0) {
      return;
    }
    const sections = ROCK_IDS.map(rockId => sectionRefs.current[rockId]).filter(
      (el): el is HTMLDivElement => Boolean(el)
    );
    if (sections.length === 0) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        const topMost = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        const rockId = topMost?.target.getAttribute('data-rock-id') as RockId | null;
        if (rockId) {
          setActiveRockId(rockId);
        }
      },
      { rootMargin: `-${headerHeight + 8}px 0px -70% 0px`, threshold: 0 }
    );
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [headerHeight, payload]);

  const handleTabSelect = (value: string) => {
    const rockId = value as RockId;
    setActiveRockId(rockId);
    sectionRefs.current[rockId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goToGheInputs = () => {
    navigate({ pathname: '/ghe-inputs', search: location.search });
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem' }}>
        <Spin data-testid="ghe-analogue-tables-loading" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        data-testid="ghe-analogue-tables-error"
      />
    );
  }

  const tables = Array.isArray(payload?.tables)
    ? payload?.tables || []
    : Array.isArray(
        (payload as GheAnalogueTablesPayload & { results?: GheAnalogueTablesPayload['tables'] })?.results
      )
    ? (payload as GheAnalogueTablesPayload & { results?: GheAnalogueTablesPayload['tables'] }).results || []
    : [];

  const emptyState = (
    <div
      data-testid="ghe-analogue-tables-empty"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '48px 32px',
      }}
    >
      <EmptyChartIllustration style={{ width: 320 }} />
      <p style={{ margin: 0, fontSize: 'var(--ds-font-size-lg)', fontWeight: 500, color: 'var(--ds-emerald-zinc-700)' }}>
        No analogue tables yet
      </p>
      <p style={{ margin: 0, fontSize: 'var(--ds-font-size-md)', color: 'var(--ds-emerald-zinc-400)', textAlign: 'center', maxWidth: 340, lineHeight: 1.5 }}>
        Save GHE inputs on "GHE Inputs" page to see the Analogue Tables.
      </p>
    </div>
  );

  if (!payload || tables.length === 0) {
    return emptyState;
  }

  const tableByRockId = new Map(tables.map(table => [table.rock_id, table]));
  const availableTables = tables.filter(table => table.rows.length > 0);

  if (availableTables.length === 0) {
    return emptyState;
  }

  return (
    <div data-testid="ghe-analogue-tables-tab" className={styles.tab}>
      <div ref={headerRef} className={navStyles.stickyTop}>
        <div className={navStyles.titleRow}>
          <Typography.Title level={3} style={{ margin: 0 }}>Analogue Samples</Typography.Title>
          {actions}
        </div>
        <div className={navStyles.subHeader}>
          <Tabs value={activeRockId} onChange={handleTabSelect}>
            <TabList>
              {ROCK_IDS.map(rockId => (
                <Tab key={rockId} value={rockId}>
                  {rockId}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </div>
      </div>
      <div className={styles.content}>
        {ROCK_IDS.map(rockId => {
          const table = tableByRockId.get(rockId);
          const isAvailable = !!table && table.rows.length > 0;
          return (
            <div
              key={rockId}
              data-rock-id={rockId}
              ref={el => {
                sectionRefs.current[rockId] = el;
              }}
              style={{ scrollMarginTop: headerHeight + 8 }}
            >
              {isAvailable ? (
                <GheTargetAnalogueTable table={table} />
              ) : (
                <Alert
                  type="info"
                  showIcon
                  data-testid={`ghe-analogue-unavailable-${rockId}`}
                  style={{ marginBottom: '0.75rem' }}
                  message={
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                      <span>{`${rockId} analogue data unavailable for current workflow inputs.`}</span>
                      <Button variant="secondary" onClick={goToGheInputs}>
                        Go to GHE Inputs
                      </Button>
                    </div>
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
